/**
 * Vue Composable for Deposit Monitoring
 * Polls wallet balance after payment initiation
 */

import { ref, computed, onUnmounted } from 'vue';
import {
  getBalance,
  getTokenBalance,
  DEFAULT_RPC_URLS,
  type WalletBalance
} from '@tokenbox/wallet-kit';

export type MonitorStatus = 'idle' | 'monitoring' | 'completed' | 'timeout' | 'error';

export interface CurrencyConfig {
  id: string;
  code: string;
  symbol: string;
  type: 'native' | 'erc20';
  decimals: number;
  contractAddress?: string;
  contractAddresses?: Record<string, string>;
}

export interface UseDepositMonitorOptions {
  address: string;
  rpcUrl?: string;
  currency?: CurrencyConfig;
  chain?: string;
  pollInterval?: number; // milliseconds (default: 10000)
  timeout?: number; // milliseconds (default: 900000 - 15 minutes)
  minBalance?: bigint; // minimum balance to consider as deposit received
  onDepositReceived?: (balance: WalletBalance) => void;
  onTimeout?: () => void;
  onError?: (error: Error) => void;
}

export function useDepositMonitor(options: UseDepositMonitorOptions) {
  const {
    address,
    rpcUrl = DEFAULT_RPC_URLS.sepolia,
    currency,
    chain = 'base_sepolia',
    pollInterval = 10000, // 10 seconds
    timeout = 900000, // 15 minutes
    minBalance = 0n,
    onDepositReceived,
    onTimeout,
    onError
  } = options;

  // State
  const status = ref<MonitorStatus>('idle');
  const currentBalance = ref<WalletBalance | null>(null);
  const previousBalance = ref<bigint>(0n);
  const error = ref<Error | null>(null);
  const elapsedTime = ref<number>(0);

  let pollIntervalId: NodeJS.Timeout | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  let startTime: number = 0;

  // Resolve contract address for ERC20 tokens
  const resolveContractAddress = (): string | undefined => {
    if (!currency || currency.type === 'native') return undefined;

    if (currency.contractAddresses) {
      const normalizedChain = chain.toLowerCase();
      return (
        currency.contractAddresses[normalizedChain] ??
        currency.contractAddresses[chain] ??
        currency.contractAddresses[normalizedChain.replace(/-/g, '_')]
      );
    }

    return currency.contractAddress;
  };

  // Check balance
  const checkBalance = async (): Promise<void> => {
    try {
      let balance: WalletBalance;

      if (currency && currency.type === 'erc20') {
        const tokenAddress = resolveContractAddress();
        if (!tokenAddress) {
          throw new Error(`Token address not found for ${currency.code} on ${chain}`);
        }
        balance = await getTokenBalance(address, tokenAddress, rpcUrl);
      } else {
        balance = await getBalance(address, rpcUrl);
      }

      currentBalance.value = balance;

      // Check if deposit received (balance increased and meets minimum)
      if (
        balance.balanceWei > previousBalance.value &&
        balance.balanceWei >= minBalance
      ) {
        handleDepositReceived(balance);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      error.value = errorObj;
      status.value = 'error';
      onError?.(errorObj);
      stopMonitoring();
    }
  };

  // Handle deposit received
  const handleDepositReceived = (balance: WalletBalance): void => {
    status.value = 'completed';
    stopMonitoring();
    onDepositReceived?.(balance);
  };

  // Handle timeout
  const handleTimeout = (): void => {
    status.value = 'timeout';
    stopMonitoring();
    onTimeout?.();
  };

  // Start monitoring
  const startMonitoring = async (): Promise<void> => {
    if (status.value === 'monitoring') {
      console.warn('Monitoring already started');
      return;
    }

    status.value = 'monitoring';
    startTime = Date.now();
    elapsedTime.value = 0;
    error.value = null;

    // Get initial balance
    await checkBalance();
    previousBalance.value = currentBalance.value?.balanceWei || 0n;

    // Start polling
    pollIntervalId = setInterval(async () => {
      await checkBalance();
      elapsedTime.value = Date.now() - startTime;
    }, pollInterval);

    // Set timeout
    timeoutId = setTimeout(() => {
      handleTimeout();
    }, timeout);
  };

  // Stop monitoring
  const stopMonitoring = (): void => {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (status.value === 'monitoring') {
      status.value = 'idle';
    }
  };

  // Reset to initial state
  const reset = (): void => {
    stopMonitoring();
    status.value = 'idle';
    currentBalance.value = null;
    previousBalance.value = 0n;
    error.value = null;
    elapsedTime.value = 0;
  };

  // Manual balance check
  const refresh = async (): Promise<void> => {
    await checkBalance();
  };

  // Computed
  const isMonitoring = computed(() => status.value === 'monitoring');
  const isCompleted = computed(() => status.value === 'completed');
  const isTimedOut = computed(() => status.value === 'timeout');
  const hasError = computed(() => status.value === 'error');
  const progress = computed(() => {
    if (timeout === 0) return 0;
    return Math.min((elapsedTime.value / timeout) * 100, 100);
  });

  // Cleanup on unmount
  onUnmounted(() => {
    stopMonitoring();
  });

  return {
    // State
    status: computed(() => status.value),
    currentBalance: computed(() => currentBalance.value),
    error: computed(() => error.value),
    elapsedTime: computed(() => elapsedTime.value),
    progress,

    // Computed flags
    isMonitoring,
    isCompleted,
    isTimedOut,
    hasError,

    // Methods
    startMonitoring,
    stopMonitoring,
    reset,
    refresh
  };
}
